/**
 * The coach-mark tour host. Rendered once by the layout route, above every
 * screen, and driven entirely by the URL:
 *
 *   /ordering/cart?tour=lesson-1&step=l1-02
 *
 * The URL carries the *step id*, never an index, so inserting a step cannot
 * teleport an in-progress learner to the wrong place, and a half-finished
 * lesson survives a reload or a shared link.
 */
import type { CSSProperties } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { MODULE_REGISTRY } from '@/content/module-registry'
import { resolveContentTemplates } from '@/content/templates'
import { isLessonInCurriculum } from '@/dashboard/derive'
import { DEFAULT_LANG, type Lang, useLang } from '@/hooks/use-lang'
import { playBeep } from '@/lib/beep'
import { CoachmarkCard } from '@/player/coachmark-card'
import { useLessonProgress } from '@/player/lesson-progress'
import { hasUnguidedStage, loadLesson, type TourLesson } from '@/player/tour-content'
import { useDraggableCard } from '@/player/use-draggable-card'
import { type TargetRect, useTargetRect } from '@/player/use-target-rect'
import type { CoachmarkStep } from '@/schema'
import { carrySessionParams } from '@/session/carry-params'
import { getSessionIdentity } from '@/session/identity'

const CARD_MAX = 360

function useWatchAndAdvance(
  watchTarget: string | undefined,
  transitioning: boolean,
  onNext: () => void,
) {
  useEffect(() => {
    if (!watchTarget || transitioning) return
    const el = document.querySelector(`[data-tour="${watchTarget}"]`)
    if (!el) return
    const initial = el.textContent?.trim() ?? ''
    let fired = false
    const check = () => {
      if (fired) return
      const current = el.textContent?.trim() ?? ''
      if (current !== '' && current !== initial) {
        fired = true
        onNext()
      }
    }
    const observer = new MutationObserver(check)
    observer.observe(el, { childList: true, subtree: true, characterData: true })
    // Safety: disconnect if the watched condition is never met within 30 s,
    // preventing an indefinitely-running observer from leaking across steps.
    const timeout = window.setTimeout(() => observer.disconnect(), 30_000)
    return () => {
      clearTimeout(timeout)
      observer.disconnect()
    }
  }, [watchTarget, transitioning, onNext])
}
const GAP = 14
const FADE_MS = 300 // overlay fade-out duration
const SETTLE_MS = 500 // how long after a step change before the overlay fades in
// How long a step that auto-opens a UI element waits before fading back in.
const CLICK_ON_ENTER_SETTLE_MS = 400
const SCRIM_BG = 'bg-[oklch(0_0_0/0.15)]'
// Halo the spotlight leaves around the target on each side.
const SPOTLIGHT_PAD = 6
const SPOTLIGHT_GLOW = 'tour-glow 1.4s ease-in-out infinite'

/**
 * Smallest height worth bounding a card to before overlap is the lesser evil.
 *
 * Both of the card's chrome rows are `shrink-0`: the header (lesson label, step
 * counter, progress bar) measures 84px and the footer (touch-height nav buttons)
 * 80px, so 164px of any `maxHeight` is spoken for before a single word of copy
 * is laid out. Only the scroller between them is `flex-1 min-h-0`, so whatever
 * is left over is what the learner actually reads — and at 0 it still renders,
 * silently clipped, which is why this failed quietly rather than overflowing.
 *
 * The previous 120 counted the header and forgot the footer, so every scan step
 * whose viewfinder sits high (stocktake l3-01/04/05/12, l2-04/09, l4-09s*, and
 * the same shape across receiving/returns/soh/ordering) passed the "usable room"
 * test with a scroller of 0-35px and showed half a headline.
 *
 * 240 = 164 of chrome + ~76 for a two-line headline and a line of body. Below
 * that, bounding the card buys nothing: let it keep its class cap and overlap
 * the target, which for these `passThrough` scan steps still leaves the lower
 * half of the viewfinder tappable.
 */
const MIN_USABLE_CARD = 240

/**
 * The same floor for a `compact` card, which is a different shape.
 *
 * `compact` drops the lesson label, the step counter and the progress bar, so
 * its chrome is ~100px rather than 164px, and a real one measures 147px tall.
 * Judging it against 240 meant the bound never fired for the only four steps
 * that use it — the date-picker steps — and they fell through to the unbounded
 * pin, which is the whole of Fix List row 7: the card sat at the top of the
 * screen covering the picker's "Select date" header.
 *
 * 140 = ~100 of chrome + a headline and a line of body.
 */
const MIN_USABLE_COMPACT_CARD = 140

/**
 * Returns the visual viewport dimensions so the overlay tracks the area
 * actually visible to the user (above the software keyboard).
 *
 * On iOS Safari, window.innerHeight does NOT shrink when the keyboard opens —
 * only visualViewport.height does. offsetTop is the distance from the layout
 * viewport top to the visual viewport top; it is > 0 when iOS scrolls content
 * up to keep the focused input visible. Pinning the overlay to these values
 * keeps getBoundingClientRect() coordinates (visual-viewport-relative) in sync
 * with the overlay's own coordinate space.
 */
function useVisualViewport(): { height: number; offsetTop: number } {
  const [vp, setVp] = useState(() => ({
    height: window.visualViewport?.height ?? window.innerHeight,
    offsetTop: window.visualViewport?.offsetTop ?? 0,
  }))
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const update = () => setVp({ height: vv.height, offsetTop: vv.offsetTop })
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [])
  return vp
}

/**
 * Where the coachmark card sits — extracted as a pure function so it can be
 * unit-tested independently of the component.
 */
type CardPositionArgs = {
  rect: { top: number; height: number } | null
  vpHeight: number
  topInset: number
  cardSpace: number
  minUsable: number
  mustNotOverlap: boolean
}

function cardPositionBelow(args: CardPositionArgs): CSSProperties {
  const { rect, vpHeight, topInset, cardSpace, minUsable, mustNotOverlap } = args
  const targetBottom = (rect?.top ?? 0) + (rect?.height ?? 0)
  const top = Math.max(
    Math.min(targetBottom + GAP, vpHeight - cardSpace),
    targetBottom + SPOTLIGHT_PAD + 1,
  )
  const room = vpHeight - top - GAP
  if (room < minUsable) {
    if (mustNotOverlap && rect !== null) {
      const roomAbove = rect.top - SPOTLIGHT_PAD - GAP - topInset
      if (roomAbove > room) {
        return { left: 0, right: 0, top: topInset, maxHeight: Math.max(0, roomAbove) }
      }
      return { left: 0, right: 0, top, maxHeight: Math.max(0, room) }
    }
    return { left: 0, right: 0, bottom: GAP }
  }
  return room < cardSpace ? { left: 0, right: 0, top, maxHeight: room } : { left: 0, right: 0, top }
}

function cardPositionAbove(args: CardPositionArgs, bottomFromTarget: number): CSSProperties {
  const { rect, vpHeight, topInset, cardSpace, minUsable, mustNotOverlap } = args
  if (rect !== null && bottomFromTarget > vpHeight - cardSpace) {
    const room = rect.top - GAP - topInset
    if (room >= minUsable) {
      return { left: 0, right: 0, top: topInset, maxHeight: room }
    }
    if (mustNotOverlap) {
      return { left: 0, right: 0, top: topInset, maxHeight: Math.max(0, room) }
    }
    return { left: 0, right: 0, top: topInset }
  }
  return { left: 0, right: 0, bottom: Math.min(bottomFromTarget, vpHeight - cardSpace) }
}

export function coachmarkPosition(args: {
  narrative: boolean
  placement: string | undefined
  belowTarget: boolean
  rect: { top: number; height: number } | null
  vpHeight: number
  /** Height of any fixed header above the overlay (e.g. the practice bar).
   *  Cards must not overlap this region, so positioning is clamped below it. */
  topInset: number
  /** The card is in its short `compact` shape, so it needs less room to be
   *  worth bounding. Only the date-picker steps set this. */
  compact?: boolean
  /** The learner has to reach the target itself — it is a field they must type
   *  into — so the two overlap escape hatches below must not fire. Covering a
   *  viewfinder is harmless; covering an input is the end of the lesson. */
  mustNotOverlap?: boolean
}): CSSProperties {
  const {
    narrative,
    placement,
    belowTarget,
    rect,
    vpHeight,
    topInset,
    compact = false,
    mustNotOverlap = false,
  } = args
  if (narrative) {
    if (placement === 'top') return { left: 0, right: 0, top: topInset }
    if (placement === 'bottom')
      return { left: 0, right: 0, bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }
    return {
      left: 0,
      right: 0,
      top: Math.round((topInset + vpHeight) / 2),
      transform: 'translateY(-50%)',
    }
  }
  const cardSpace = Math.round(vpHeight / 2)
  const minUsable = compact ? MIN_USABLE_COMPACT_CARD : MIN_USABLE_CARD
  const posArgs: CardPositionArgs = {
    rect,
    vpHeight,
    topInset,
    cardSpace,
    minUsable,
    mustNotOverlap,
  }
  if (belowTarget) return cardPositionBelow(posArgs)
  const bottomFromTarget = vpHeight - (rect?.top ?? 0) + GAP
  return cardPositionAbove(posArgs, bottomFromTarget)
}

function placementBelow(placement: string | undefined, measured: boolean): boolean {
  if (placement === 'top') return false
  if (placement === 'bottom') return true
  return measured
}

/**
 * Whether the spotlight is visual-only — clicks fall through to the real
 * element instead of being swallowed and used to advance the tour. True for
 * steps gated on typed input, explicit pass-through steps, and steps that
 * advance by watching the DOM.
 */
function isPassiveSpotlight(step: CoachmarkStep): boolean {
  return !!(
    step.requiresInput ||
    step.requiresInputPattern ||
    step.passThrough ||
    step.watchAndAdvance
  )
}

/**
 * The card's lesson caption: the sub-module title minus its "Lesson N:" prefix.
 *
 * Exported for the same reason as `coachmarkPosition` — it is pure, and its
 * fallbacks (a lesson that has not loaded, a title that is nothing but the
 * prefix) are states no shipped content file can produce.
 */
export function lessonLabelOf(lesson: TourLesson | null): string {
  const title = lesson?.subModule.title
  if (!title) return 'Lesson'
  return title.replace(/^lesson\s+\d+[:.]\s*/i, '').trim() || title
}

/**
 * Whether the step's required interaction is already satisfied, which is what
 * lets the card offer Next instead of waiting on the learner.
 *
 * `watchAndAdvance` and `advanceOnClick` are never satisfied here: those steps
 * advance themselves (from the DOM observer / a delegated click), so the card
 * must never offer a manual way past.
 */
function interactionCompleteOf(step: CoachmarkStep, inputSatisfied: boolean): boolean {
  if (step.watchAndAdvance || step.advanceOnClick) return false
  const needsInput = !!(step.requiresInput || step.requiresInputPattern)
  return (needsInput && inputSatisfied) || !!step.passThrough
}

/**
 * The scrim and spotlight for a step anchored on a live element: four panels
 * boxing the target in, plus the highlight itself.
 *
 * Extracted from `TourOverlay` so the host component stays within its
 * cognitive-complexity budget — it is presentational and holds no state.
 */
function TourSpotlight({
  step,
  rect,
  label,
  onSpotlightClick,
}: Readonly<{
  step: CoachmarkStep
  rect: TargetRect
  label: string
  onSpotlightClick: () => void
}>) {
  /*
   * Dark so it is visually obvious only the highlighted element is
   * interactive. `watchAndAdvance` steps rely on the learner freely
   * interacting with the element (e.g. opening a dropdown, picking an option)
   * so their scrim is visual-only — pointer events pass through everywhere.
   */
  const scrim = `absolute ${SCRIM_BG} ${
    step.watchAndAdvance ? 'pointer-events-none' : 'pointer-events-auto'
  }`
  const halo: CSSProperties = {
    top: rect.top - SPOTLIGHT_PAD,
    left: rect.left - SPOTLIGHT_PAD,
    width: rect.width + SPOTLIGHT_PAD * 2,
    height: rect.height + SPOTLIGHT_PAD * 2,
    animation: SPOTLIGHT_GLOW,
  }

  return (
    <>
      <div
        className={scrim}
        style={{ top: 0, left: 0, right: 0, height: Math.max(0, rect.top - SPOTLIGHT_PAD) }}
      />
      <div
        className={scrim}
        style={{
          top: rect.top - SPOTLIGHT_PAD,
          left: 0,
          width: Math.max(0, rect.left - SPOTLIGHT_PAD),
          height: rect.height + SPOTLIGHT_PAD * 2,
        }}
      />
      <div
        className={scrim}
        style={{
          top: rect.top - SPOTLIGHT_PAD,
          left: rect.left + rect.width + SPOTLIGHT_PAD,
          right: 0,
          height: rect.height + SPOTLIGHT_PAD * 2,
        }}
      />
      <div
        className={scrim}
        style={{ top: rect.top + rect.height + SPOTLIGHT_PAD, left: 0, right: 0, bottom: 0 }}
      />

      {isPassiveSpotlight(step) ? (
        <div aria-hidden="true" className="absolute rounded-xl pointer-events-none" style={halo} />
      ) : (
        <button
          type="button"
          aria-label={label}
          onClick={onSpotlightClick}
          className="absolute cursor-pointer rounded-xl pointer-events-auto"
          style={halo}
        />
      )}
    </>
  )
}

/** Build the URL for a step: its screen, the tour cursor, and any setup params. */
function stepUrl(
  step: CoachmarkStep,
  tourId: string,
  currentParams?: URLSearchParams,
  lang: Lang = DEFAULT_LANG,
): string {
  const search = new URLSearchParams({ tour: tourId, step: step.id })
  for (const [k, v] of Object.entries(step.params ?? {})) search.set(k, v)
  // Identity and language belong to the session, not to the step — see
  // `@/session/carry-params` for the navigation loop that dropping them caused.
  carrySessionParams(currentParams, search)
  // The URL may have lost `lang` to a bare navigate() before we got here, in
  // which case `lang` is the live choice resolved from i18next. Re-attach it.
  if (lang !== DEFAULT_LANG && !search.has('lang')) search.set('lang', lang)
  return `${step.screen}?${search.toString()}`
}

export function TourOverlay() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { height: vpHeight, offsetTop: vpOffsetTop } = useVisualViewport()
  const lang = useLang()
  const { t } = useTranslation('player')
  const [lesson, setLesson] = useState<TourLesson | null>(null)
  const [inputSatisfied, setInputSatisfied] = useState(false)
  // Start hidden; fades in once the first step's screen has settled.
  const [transitioning, setTransitioning] = useState(true)
  // Height of the practice bar so card positioning never overlaps it.
  const [topInset, setTopInset] = useState(0)
  useEffect(() => {
    const bar = document.querySelector('[data-practice-bar]')
    if (!bar) return
    const obs = new ResizeObserver(() => setTopInset((bar as HTMLElement).offsetHeight))
    obs.observe(bar)
    setTopInset((bar as HTMLElement).offsetHeight)
    return () => obs.disconnect()
  }, [])

  /**
   * The step-advance scheduled by `withFade` / `handleSpotlightClick`.
   *
   * It has to be cancellable. Both schedule the advance on a timer — 300ms for
   * the fade, up to 1500ms for a step that waits on a drawer animation — and
   * during that window the overlay is faded out, which is exactly when a
   * learner who meant to leave taps "Exit lesson". Without this ref the
   * orphaned timer still fires after `exit()` has navigated away and pulls the
   * learner straight back into the tour at the NEXT step.
   */
  const pendingAdvance = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelPendingAdvance = useCallback(() => {
    if (pendingAdvance.current === null) return
    clearTimeout(pendingAdvance.current)
    pendingAdvance.current = null
  }, [])
  // A tour left by unmount (route change, lesson end) must not fire either.
  useEffect(() => cancelPendingAdvance, [cancelPendingAdvance])

  const tourId = params.get('tour')
  const stepId = params.get('step')

  useEffect(() => {
    if (!tourId) {
      setLesson(null)
      return
    }
    let live = true
    loadLesson(tourId, lang)
      .then((l) => {
        if (!live) return
        // A lesson outside this role's curriculum must not play just because
        // someone has the link. The module page already filters these out of
        // the list; without the same check here the lesson stayed one deep
        // link away, which is how an RA reached the manager-only create-task
        // walkthrough. An unconfigured role still gets everything, matching
        // `isLessonInCurriculum`.
        const roleId = getSessionIdentity()?.roleId ?? null
        if (l && !isLessonInCurriculum(roleId, l.moduleId, tourId)) {
          setLesson(null)
          return
        }
        setLesson(l)
      })
      .catch(() => {
        if (live) setLesson(null)
      })
    return () => {
      live = false
    }
  }, [tourId, lang])

  const steps = lesson?.steps ?? []
  // Does this lesson have an unguided stage after the tour? The sub-module is
  // already loaded to render the tour, so this is free — and `finish()` needs
  // it to say whether the guided stage completes the lesson on its own.
  const requiresUnguided = hasUnguidedStage(lesson?.subModule)
  const isTour =
    !!lesson?.moduleId &&
    !!tourId &&
    MODULE_REGISTRY[lesson.moduleId]?.subModules[tourId]?.type === 'tour'
  const index = Math.max(
    0,
    steps.findIndex((s) => s.id === stepId),
  )
  const step = steps[index]
  // The computed slot is a starting point, not a verdict: the learner can drag
  // any card clear of whatever it happens to cover. Resets on every step.
  // Called before the `!step` bail-out below, because hooks cannot be conditional.
  const {
    offset: dragOffset,
    dragging,
    nodeRef: cardRef,
    dragHandlers,
  } = useDraggableCard(step?.id ?? '')
  const isExplore = !!step?.explore
  const rect = useTargetRect(step?.target)

  // Drive the app to the screen this step is anchored on.
  // Suppressed during transitions so that el.click() navigations in handleSpotlightClick
  // are not immediately overridden before goTo fires with the next step's URL.
  useEffect(() => {
    if (!step || !tourId || transitioning) return
    const want = stepUrl(step, tourId, params, lang)
    if (`${location.pathname}${location.search}` === want) return
    void navigate(want, { replace: true })
  }, [step, location.pathname, location.search, navigate, tourId, params, transitioning, lang])

  // Each time the active step changes, hide the overlay immediately and then
  // fade it back in after SETTLE_MS — giving the screen time to render and
  // any animations (drawers, transitions) time to complete first.
  useEffect(() => {
    if (!step?.id) return
    setTransitioning(true)
    setInputSatisfied(false)
    // If this step auto-opens a UI element (e.g. a date picker), click it now
    // so the dialog has time to mount before the overlay fades back in.
    if (step.clickOnEnter) {
      const el = document.querySelector(`[data-tour="${step.clickOnEnter}"]`)
      if (el instanceof HTMLElement) el.click()
    }
    if (step.scrollTarget) {
      const el = document.querySelector(`[data-tour="${step.scrollTarget}"]`)
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    const settle = step.clickOnEnter ? CLICK_ON_ENTER_SETTLE_MS : SETTLE_MS
    const t = setTimeout(() => setTransitioning(false), settle)
    return () => clearTimeout(t)
  }, [step?.id, step?.clickOnEnter, step?.scrollTarget])

  const goTo = useCallback(
    (i: number) => {
      const next = steps[i]
      if (!next || !tourId) return
      void navigate(stepUrl(next, tourId, params, lang))
    },
    [steps, navigate, tourId, params, lang],
  )

  const exit = useCallback(() => {
    cancelPendingAdvance()
    const navId = lesson?.moduleId ? MODULE_REGISTRY[lesson.moduleId]?.navId : undefined
    const dest = isTour && navId ? `/module/${navId}` : `/lesson-detail/${tourId}`
    void navigate(dest, { replace: true })
  }, [navigate, tourId, isTour, lesson?.moduleId, cancelPendingAdvance])

  /**
   * Running past the last step finishes the GUIDED stage — and only that. When
   * the lesson also has tasks, `requiresUnguided` keeps it short of complete
   * until `TaskOverlay` records the unguided stage too; when it has none
   * (19 of the 29 sub-modules), this one call completes the lesson.
   *
   * The SAME `requiresUnguided` then decides where the learner lands, so the
   * screen they see can never contradict what was just recorded:
   *
   * - lesson has an unguided stage → Screen A, which says so in as many words
   * - lesson has none → Screen B, because this finished it
   *
   * `replace` on both: the last step is not somewhere Back should return to.
   */
  const finish = useCallback(() => {
    if (!isTour) {
      try {
        if (tourId) useLessonProgress.getState().markStageDone(tourId, 'guided', requiresUnguided)
      } catch {
        // localStorage unavailable (e.g. private browsing or restricted env) —
        // still navigate so the learner is not stuck on the last step.
      }
    }
    const ending = requiresUnguided ? 'guided-complete' : 'complete'
    void navigate(`/lesson/${tourId}/${ending}`, { replace: true })
  }, [navigate, tourId, isTour, requiresUnguided])

  // Fade out, then execute the navigation action after the fade completes.
  const withFade = useCallback(
    (action: () => void) => {
      setTransitioning(true)
      cancelPendingAdvance()
      pendingAdvance.current = setTimeout(action, FADE_MS)
    },
    [cancelPendingAdvance],
  )

  const onNext = useCallback(() => {
    if (step?.beepOnNext) playBeep()
    withFade(() => {
      if (index >= steps.length - 1) finish()
      else goTo(index + 1)
    })
  }, [step?.beepOnNext, withFade, index, steps.length, finish, goTo])

  const onPrevious = useCallback(() => {
    withFade(() => goTo(index - 1))
  }, [withFade, goTo, index])

  const handleSpotlightClick = useCallback(() => {
    if (step?.delayAdvanceMs && step.target) {
      // Fade out immediately so the element's animation (drawer, picker) plays
      // without the overlay on top. Then navigate after the animation finishes.
      setTransitioning(true)
      const el = document.querySelector(`[data-tour="${step.target}"]`)
      if (el instanceof HTMLElement) el.click()
      cancelPendingAdvance()
      pendingAdvance.current = setTimeout(() => {
        if (index >= steps.length - 1) finish()
        else goTo(index + 1)
      }, step.delayAdvanceMs)
    } else {
      onNext()
    }
  }, [step, index, steps.length, finish, goTo, onNext, cancelPendingAdvance])

  // When a step requires the learner to type a specific value, listen to the
  // real input element and unlock the Next button once the value matches.
  // Depends on `transitioning` so the effect re-runs after the settle delay —
  // by then the bottom sheet has mounted and the input is in the DOM.
  useEffect(() => {
    if ((!step?.requiresInput && !step?.requiresInputPattern) || !step.target || transitioning)
      return
    const container = document.querySelector(`[data-tour="${step.target}"]`)
    const input: HTMLInputElement | HTMLTextAreaElement | null =
      container instanceof HTMLInputElement || container instanceof HTMLTextAreaElement
        ? container
        : (container?.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea') ??
          null)
    if (!input) return
    const pattern = step.requiresInputPattern ? new RegExp(step.requiresInputPattern) : null
    const check = () => {
      const val = input.value.trim()
      let satisfied = false
      if (step.requiresInput) {
        satisfied = val.toLowerCase().includes(step.requiresInput.toLowerCase())
      } else if (pattern) {
        satisfied = pattern.test(val)
      }
      setInputSatisfied(satisfied)
    }
    check() // catch any value already in the field when the listener attaches
    // 'input' fires on every keystroke and covers paste; 'change' is a fallback
    // for iOS Safari where React controlled inputs occasionally suppress the
    // native input event, and also fires on autofill completion. The poll is a
    // safety net for programmatic value assignment (simulation scaffolding,
    // older Android WebView IMEs that don't fire input per keystroke) — events
    // alone are not sufficient on shop-floor handsets.
    input.addEventListener('input', check)
    input.addEventListener('change', check)
    const poll = window.setInterval(check, 500)
    return () => {
      input.removeEventListener('input', check)
      input.removeEventListener('change', check)
      window.clearInterval(poll)
    }
  }, [step?.requiresInput, step?.requiresInputPattern, step?.target, transitioning])

  // Watch a DOM element's text content and auto-advance when it changes.
  // Used for steps where completion is signalled by a UI update (e.g. a date
  // appearing in the delivery-date field after the picker closes).
  useWatchAndAdvance(step?.watchAndAdvance, transitioning, onNext)

  // Advance when the learner clicks a specific element (e.g. a dialog's confirm
  // button). Delegated on the document in the capture phase so it fires even for
  // elements that mount after the step loads (like a dialog opened by typing) and
  // even if the element unmounts on click. The element's own handler still runs.
  useEffect(() => {
    if (!step?.advanceOnClick || transitioning) return
    const target = step.advanceOnClick
    const handler = (e: Event) => {
      const el = e.target as Element | null
      if (el?.closest(`[data-tour="${target}"]`)) onNext()
    }
    document.addEventListener('click', handler, true)
    return () => document.removeEventListener('click', handler, true)
  }, [step?.advanceOnClick, transitioning, onNext])

  if (!tourId || !step) return null

  const narrative = rect === null
  // Use vpHeight (visual viewport) for the midpoint test so the card appears on
  // the correct side of the spotlit element when the keyboard is open.
  const rawBelowTarget = rect !== null && rect.top + rect.height / 2 < vpHeight * 0.5
  const belowTarget = placementBelow(step.placement, rawBelowTarget)
  // Hoisted because the card needs the `maxHeight` this computes, not just the
  // wrapper: a cap on the wrapper alone would let the card overflow it.
  const cardPosition = coachmarkPosition({
    narrative,
    placement: step.placement,
    belowTarget,
    rect,
    vpHeight,
    topInset,
    compact: step.compact === true,
    // The same predicate `isPassiveSpotlight` uses: these are the steps whose
    // only way forward is typing into the target, so the card must leave it
    // reachable.
    mustNotOverlap: step.requiresInput !== undefined || step.requiresInputPattern !== undefined,
  })
  return (
    <div
      data-tour-overlay
      className="fixed inset-0 z-100 transition-opacity duration-300"
      style={{
        // Pin the overlay to the visual viewport so getBoundingClientRect()
        // coordinates (visual-viewport-relative) stay in sync with the overlay's
        // coordinate space when the software keyboard shifts offsetTop.
        top: vpOffsetTop,
        left: 0,
        right: 0,
        height: vpHeight,
        opacity: transitioning ? 0 : 1,
        // Container never blocks pointer events — children handle all blocking so
        // we can selectively allow only the highlighted target through.
        pointerEvents: 'none',
      }}
      // Stop pointerdown from reaching document-level listeners (e.g. Radix's
      // DismissableLayer). Without this, tapping any overlay element — the card,
      // the scrim panels, or the spotlight — is treated as an "outside click" by
      // any open sheet/dialog and closes it, even when the step requires the
      // drawer to stay open. pointerdown and click are separate events, so
      // stopping only pointerdown does not affect the overlay's own click handlers.
      onPointerDown={(e) => e.stopPropagation()}
      aria-live="polite"
    >
      {/* Invisible interaction blocker. Transparent steps render no scrim, so
       * without this the bright app bottom sheet below stays tappable and a
       * stray tap (e.g. "Yes, please hold DO") desyncs the guided flow. The
       * blocker is pointer-events-auto so it absorbs those taps; the card is
       * rendered later in the DOM and stacks above it, so its buttons remain
       * the only way forward. */}
      {step.blockInteraction && (
        <div aria-hidden="true" className="absolute inset-0 h-full w-full pointer-events-auto" />
      )}

      {/* Scrim + spotlight — skipped entirely for transparent/pass-through steps */}
      {!step.transparent &&
        (rect === null ? (
          // Narrative: full-screen clickable scrim blocks and advances.
          <button
            type="button"
            aria-label={t('continueLesson')}
            onClick={onNext}
            className={`absolute inset-0 h-full w-full cursor-default ${SCRIM_BG} pointer-events-auto`}
          />
        ) : (
          <TourSpotlight
            step={step}
            rect={rect}
            label={t('continueLesson')}
            onSpotlightClick={handleSpotlightClick}
          />
        ))}

      <div
        data-coachmark-wrapper
        // `inset-x-0`: the card's width comes from the wrapper, never from the
        // target rect. Every `coachmarkPosition` branch returns left/right
        // today, but a branch that forgot to would let this absolutely
        // positioned box shrink-to-fit — and with the global
        // `overflow-wrap: anywhere`, collapse to a one-glyph column.
        className="pointer-events-none absolute inset-x-0 flex justify-center px-4"
        style={cardPosition}
      >
        <div
          ref={cardRef}
          {...dragHandlers}
          className="pointer-events-auto w-full"
          style={{
            maxWidth: CARD_MAX,
            transform:
              dragOffset.x || dragOffset.y
                ? `translate3d(${dragOffset.x}px, ${dragOffset.y}px, 0)`
                : undefined,
            cursor: dragging ? 'grabbing' : 'grab',
          }}
        >
          <CoachmarkCard
            maxHeight={cardPosition.maxHeight as number | undefined}
            lessonLabel={lessonLabelOf(lesson)}
            // "Step N of M" counts EVERY step in the tour, `explore` beats
            // included, because M has to be the number of steps the learner
            // actually walks. Counting only non-`explore` steps made M a
            // different number from the lesson's length — SOH lesson 7 walks 14
            // steps and read "Step 1 of 2" — and left the progress bar frozen
            // for every orientation beat in between. `explore` still suppresses
            // the counter and the bar on its OWN step (see `CoachmarkCard`), so
            // an orientation beat is still presented un-numbered; it is simply
            // no longer missing from the total. M now agrees with
            // `listLessons().stepCount`, which the dashboard already counts
            // unfiltered to derive its minute estimate.
            stepIndex={index}
            stepIds={steps.map((s) => s.id)}
            isFirst={index === 0}
            isLast={index === steps.length - 1}
            explore={isExplore}
            compact={!!step.compact}
            emphasis={!!step.emphasis}
            headline={step.headline}
            body={resolveContentTemplates(step.body)}
            note={step.note}
            narrative={narrative}
            hideNarrativeNote={!!step.transparent || !!step.passThrough || !!step.hideNarrativeNote}
            requiresInteraction={
              (!narrative || !!step.watchAndAdvance || !!step.advanceOnClick) && !step.spotlightInfo
            }
            interactionComplete={interactionCompleteOf(step, inputSatisfied)}
            isTour={isTour}
            onPrevious={onPrevious}
            onNext={onNext}
            onExit={exit}
          />
        </div>
      </div>
    </div>
  )
}
